/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import { Box, Divider, Typography } from '@mui/material';
import type { ReactNode } from 'react';

type SnippetsSectionType = {
	headerCharacter: string | number;
	title: string;
	children: ReactNode;
	variant?: 'small' | 'medium';
	hideHorizontalLine?: boolean;
};

export function SnippetsSection( {
	headerCharacter,
	title,
	children,
	variant = 'medium',
	hideHorizontalLine = false
}: SnippetsSectionType ): ReactNode {
	const size = variant === 'medium' ? '24px' : '10px';
	const horizontalLineMargin = variant === 'medium' ? '2px 12px' : '-2px 4.5px -2px';
	const circleMarginTop = variant === 'medium' ? '0px' : '3px';
	const titleFontWeight = variant === 'medium' ? 500 : 400;
	const titleFontSize = variant === 'medium' ? 16 : 12;
	const titleColor = variant === 'medium' ? 'text.primary' : 'text.secondary';

	return (
		<Box>
			<Box display={ 'flex' } gap={ '20px' }>
				<Box
					sx={ {
						marginTop: circleMarginTop,
						backgroundColor: 'grey.300',
						borderRadius: '50%',
						width: size,
						height: size,
						textAlign: 'center'
					} }
				>
					<Typography fontWeight={ 400 } fontSize={ 15 }>
						{ headerCharacter }
					</Typography>
				</Box>
				<Typography fontWeight={ titleFontWeight } fontSize={ titleFontSize } color={ titleColor }>
					{ title }
				</Typography>
			</Box>
			<Box display={ 'flex' } gap={ '20px' } sx={ { overflow: 'hidden' } }>
				<Divider
					data-testid={ 'snippets-section-divider' }
					orientation="vertical"
					flexItem
					sx={ {
						margin: horizontalLineMargin,
						borderColor: 'grey.300',
						visibility: hideHorizontalLine ? 'hidden' : 'visible'
					} }
				/>
				<Box sx={ { margin: '10px 0 20px', flexGrow: '1', overflow: 'auto' } }>
					{ children }
				</Box>
			</Box>
		</Box>
	);
}
